import { useFetch } from '@gluedigital/ruse-fetch-extras';
import { canonicalizeVideoUrl } from '@utils/videoParser';
import { useEffect, useMemo } from 'react';
import { API_BASE } from 'src/constants/sentinel';
import { setVideoMetadata } from 'src/store/slices/sentinel';
import type { ImageVerificationResponse, ImageVerificationResult, ShowcaseData, VideoMetadata } from 'src/types/sentinel';
import { useAppDispatch, useAppSelector } from 'universal-scripts';

export function useVideoMetadata(videoUrl: string | null) {
	const dispatch = useAppDispatch();
	const storedMetadata = useAppSelector((state) => state.sentinel.metadata);
	const shouldFetch = videoUrl && !storedMetadata;
	// Query the pipeline with the canonical YouTube watch URL; the original videoUrl stays intact for display.
	const fetchedData = useFetch<VideoMetadata>(
		shouldFetch ? `${API_BASE}/video-metadata?video_url=${encodeURIComponent(canonicalizeVideoUrl(videoUrl))}` : null
	);

	useEffect(() => {
		if (fetchedData) {
			dispatch(setVideoMetadata(fetchedData));
		}
	}, [fetchedData, dispatch]);
	const result = storedMetadata || fetchedData;

	if (!result) return null;

	return {
		thumbnailUrl: result.thumbnail_url,
		description: result.description,
		publicationDate: new Date(result.publication_date),
		interactions: {
			likes: result.interaction_data.likes,
			comments: result.interaction_data.comments,
			shares: result.interaction_data.shares
		},
		channel: {
			avatarUrl: result.channel_data.avatar_url,
			name: result.channel_data.name,
			followers: result.channel_data.followers,
			isGray: result.channel_data.is_greyweb
		}
	};
}

export function usePresetVideos() {
	const fetchedData = useFetch<ShowcaseData>(`${API_BASE}/preset-videos`);
	return fetchedData;
}

export function useImageVerification(imageUrls: string[] | null): ImageVerificationResult | null {
	const shouldFetch = !!(imageUrls && imageUrls.length > 0);
	const body = useMemo(() => JSON.stringify({ image_urls: imageUrls }), [imageUrls]);
	const fetchOptions = useMemo(
		() => ({
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				// biome-ignore lint/style/useNamingConvention: HTTP header name is conventionally capitalized
				Accept: 'application/json'
			},
			body
		}),
		[body]
	);
	const response = useFetch<ImageVerificationResponse>(shouldFetch ? `${API_BASE}/verify-images` : null, fetchOptions);
	if (!response) return null;
	const executionTime = response.image_verifications.reduce((sum, it) => sum + 1000 * (it.processing_time_s ?? 0), 0);

	return {
		predictedLabel: response.predicted_label,
		confidence: response.confidence,
		executionTime: executionTime,
		provider: {
			name: response.provider.name ?? 'Trueflag AI',
			url: response.provider.page_url ?? 'https://trueflag.ai/',
			image: response.provider.thumbnail_url ?? ''
		},
		verifications: response.image_verifications.map((item) => ({
			url: item.url,
			isAiGenerated: item.is_ai_generated,
			confidence: item.confidence
		}))
	};
}
